// src/resources/customerRfm/RfmGrid.tsx
//
// "Mapa RFM": grade 5×5 (spec §6.2 item 3). Linhas = recência (R5 no topo),
// colunas = frequência (F1 à esquerda). A cor de cada célula é a do segmento
// dela, com alpha proporcional à quantidade de clientes — é a explicação
// visual da régua. Clicar filtra a lista por r/f (clicar de novo limpa).
import React, { useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { __t } from '../../i18n/t';
import { COLORS, cardSx, fmtBrl, fmtNum } from '../dashboards/shared';
import { RFM_SEGMENT_ORDER, RfmCutoffs, RfmGridCell, RfmSegment, SEGMENT_META, frequencyLabels, recencyLabels } from './segments';

interface Props {
  grid: RfmGridCell[];
  cutoffs: RfmCutoffs;
  selectedR?: number;
  selectedF?: number;
  onSelectCell: (cell: RfmGridCell) => void;
}

// Deriva o segmento de uma célula (r, f) a partir de `cutoffs.segmentRules`, que vem
// do próprio backend — não é uma régua escrita à mão aqui, só uma leitura genérica das
// regras na mesma ordem em que o backend as avalia (ver rules.ts). Usado só para preencher
// células sem clientes (não aparecem no `grid` agregado, que só tem combinações com dado).
const resolveSegment = (r: number, f: number, c: RfmCutoffs): RfmSegment => {
  const rule = c.segmentRules.find((rl) => (rl.minR == null || r >= rl.minR) && (rl.minF == null || f >= rl.minF));
  return rule?.segment ?? c.fallbackSegment;
};

const RfmGrid: React.FC<Props> = ({ grid, cutoffs, selectedR, selectedF, onSelectCell }) => {
  const rLabels = recencyLabels(cutoffs);
  const fLabels = frequencyLabels(cutoffs);

  const byKey = useMemo(() => {
    const map = new Map<string, RfmGridCell>();
    grid.forEach((cell) => map.set(`${cell.r}-${cell.f}`, cell));
    return map;
  }, [grid]);

  const maxCustomers = Math.max(1, ...grid.map((c) => c.customers));

  const rows = [5, 4, 3, 2, 1];
  const cols = [1, 2, 3, 4, 5];

  return (
    <Box sx={{ ...cardSx }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>Mapa RFM</Typography>
      <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>
        Recência (linhas) × frequência (colunas). Cada cor é um grupo.
      </Typography>

      {/* width 0 + minWidth 100%: a grade (mínimo 480px) rola dentro do card no mobile em vez de
          esticar a página (RaLayout-root do app tem min-width fit-content). */}
      <Box sx={{ overflowX: 'auto', width: 0, minWidth: '100%' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '110px repeat(5, minmax(76px, 1fr))', gap: 0.5, minWidth: 480 }}>
          <Box />
          {cols.map((f) => (
            <Typography key={f} sx={{ fontSize: 10.5, fontWeight: 700, color: COLORS.muted, textAlign: 'center', alignSelf: 'end', pb: 0.5 }}>
              {fLabels[f]}
            </Typography>
          ))}

          {rows.map((r) => (
            <React.Fragment key={r}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: COLORS.muted, alignSelf: 'center' }}>
                {rLabels[r]}
              </Typography>
              {cols.map((f) => {
                const cell = byKey.get(`${r}-${f}`);
                const segment = cell?.segment ?? resolveSegment(r, f, cutoffs);
                const customers = cell?.customers ?? 0;
                const totalValue = cell?.totalValue ?? 0;
                const meta = SEGMENT_META[segment];
                const alpha = customers > 0 ? Math.max(0.12, customers / maxCustomers) : 0.04;
                const active = selectedR === r && selectedF === f;
                return (
                  <Tooltip
                    key={f}
                    arrow
                    title={`${meta.emoji} ${__t(meta.label)} · ${fmtNum(customers)} clientes · ${fmtBrl(totalValue)}`}
                  >
                    <Box
                      onClick={() => onSelectCell(cell ?? { r, f, segment, customers: 0, totalValue: 0 })}
                      sx={{
                        borderRadius: 1.5,
                        height: 56,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                        bgcolor: `${meta.color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`,
                        border: active ? `2px solid ${meta.color}` : '2px solid transparent',
                        transition: 'all 0.15s',
                        '&:hover': { border: `2px solid ${meta.color}` },
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: customers > 0 ? COLORS.ink : COLORS.faint }}>
                        {fmtNum(customers)}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
        {RFM_SEGMENT_ORDER.map((segment) => (
          <Box key={segment} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SEGMENT_META[segment].color }} />
            <Typography sx={{ fontSize: 11.5, color: COLORS.muted }}>
              {SEGMENT_META[segment].emoji} {__t(SEGMENT_META[segment].label)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RfmGrid;
